import { STORAGE_KEYS } from "@/lib/storage-keys";

export type LastSignInMethod =
	| "wallet"
	| "file"
	| "phrase"
	| "email"
	| `provider:${"github" | "google" | "apple" | "twitter" | "discord"}`;
const METHODS = new Set([
	"wallet",
	"file",
	"phrase",
	"email",
	"provider:github",
	"provider:google",
	"provider:apple",
	"provider:twitter",
	"provider:discord",
]);
const PENDING_RECOVERY = "sigma:pending-recovery-login";
const PENDING_PROVIDER = "sigma:pending-provider-login";
export const LOGIN_METHOD_CHANGED = "sigma:login-method-changed";

export function readLastSignInMethod(): LastSignInMethod | null {
	try {
		const value = localStorage.getItem(STORAGE_KEYS.LOGIN_LAST_METHOD);
		return value && METHODS.has(value) ? (value as LastSignInMethod) : null;
	} catch {
		return null;
	}
}

/** Call only after authentication succeeds; preferences must never break login. */
export function rememberSuccessfulSignIn(method: LastSignInMethod): void {
	try {
		sessionStorage.removeItem(PENDING_RECOVERY);
		sessionStorage.removeItem(PENDING_PROVIDER);
		localStorage.setItem(STORAGE_KEYS.LOGIN_LAST_METHOD, method);
		window.dispatchEvent(new Event(LOGIN_METHOD_CHANGED));
	} catch {
		/* Storage can be unavailable in private browsing. */
	}
}

/** A pending attempt is not a successful method. Only its callback can consume it. */
export function prepareProviderMemory(
	provider: string,
	callbackURL: string,
	newUserCallbackURL: string
): { callbackURL: string; newUserCallbackURL: string } {
	const original = { callbackURL, newUserCallbackURL };
	if (!METHODS.has(`provider:${provider}`)) {
		return original;
	}
	try {
		clearPendingRecoveryMethod();
		const nonce = crypto.randomUUID();
		sessionStorage.setItem(
			PENDING_PROVIDER,
			JSON.stringify({ provider, nonce })
		);
		const decorate = (value: string) => {
			const url = new URL(value, window.location.origin);
			url.searchParams.set("login_success", nonce);
			url.searchParams.set("provider", provider);
			return url.toString();
		};
		return {
			callbackURL: decorate(callbackURL),
			newUserCallbackURL: decorate(newUserCallbackURL),
		};
	} catch {
		return original;
	}
}

/** Called after a server session read, never merely because OAuth was opened. */
export function completeProviderMemory(authenticated: boolean): void {
	if (!authenticated) {
		return;
	}
	try {
		const url = new URL(window.location.href);
		if (
			url.searchParams.has("error") ||
			url.searchParams.has("error_description")
		) {
			return;
		}
		const pending = JSON.parse(
			sessionStorage.getItem(PENDING_PROVIDER) || "null"
		);
		if (
			!pending ||
			pending.nonce !== url.searchParams.get("login_success") ||
			pending.provider !== url.searchParams.get("provider")
		) {
			return;
		}
		const method = `provider:${pending.provider}`;
		if (!METHODS.has(method)) {
			return;
		}
		sessionStorage.removeItem(PENDING_PROVIDER);
		stageRecoveryMethod(method as LastSignInMethod);
	} catch {
		/* This preference is optional. */
	}
}

export function clearPendingRecoveryMethod(): void {
	try {
		sessionStorage.removeItem(PENDING_RECOVERY);
		sessionStorage.removeItem(PENDING_PROVIDER);
	} catch {
		/* Optional preference. */
	}
}

export function stageRecoveryMethod(method: LastSignInMethod): void {
	try {
		sessionStorage.setItem(
			PENDING_RECOVERY,
			JSON.stringify({ method, createdAt: Date.now() })
		);
	} catch {
		/* Optional preference. */
	}
}

/** Recovery authentication alone is insufficient: call after usable identity proof. */
export function completeRecoveryMethod(): void {
	try {
		if (
			!["/login", "/restore", "/onboarding"].includes(window.location.pathname)
		) {
			return;
		}
		completeProviderMemory(true);
		const pending = JSON.parse(
			sessionStorage.getItem(PENDING_RECOVERY) || "null"
		);
		clearPendingRecoveryMethod();
		if (
			pending &&
			METHODS.has(pending.method) &&
			Date.now() - pending.createdAt < 30 * 60 * 1000
		) {
			rememberSuccessfulSignIn(pending.method);
		}
	} catch {
		/* Optional preference. */
	}
}
